import { FC, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Typography,
  TabsContent,
  toast,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Card,
  CardContent,
  CardHeader,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { IconRefresh, IconPlus } from "@tabler/icons-react";
import { Loader } from "../../components/Loader";
import { getPlaygroundAgents, getAvailableModels } from "../../services/evals";
import { PlaygroundAgentListItem } from "./PlaygroundAgentListItem";
import { CreateAgentForm, CreateAgentFormData } from "./CreateAgentForm";
import { LocalAgentListItem } from "./LocalAgentListItem";
import { AgentDetailsPanel } from "./AgentDetailsPanel";
import { useLocalAgents } from "../hooks/useLocalAgents";

interface PlaygroundAgentsTabProps {
  onRefresh?: () => void;
}

export const PlaygroundAgentsTab: FC<PlaygroundAgentsTabProps> = ({ onRefresh }) => {
  const [selectedAgentName, setSelectedAgentName] = useState<string | null>(null);
  const [selectedLocalAgentId, setSelectedLocalAgentId] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const {
    localAgents,
    createAgent,
    deleteAgent,
    updateAgent,
    getAgentVersions,
    restoreAgentVersion,
  } = useLocalAgents();

  // Fetch backend agents
  const {
    data: agentsData,
    isLoading: agentsLoading,
    refetch: refetchAgents,
  } = useQuery({
    queryKey: ["playgroundAgents"],
    queryFn: getPlaygroundAgents,
  });

  // Fetch available models
  const { data: availableModels } = useQuery({
    queryKey: ["availableModels"],
    queryFn: getAvailableModels,
  });

  const handleRefresh = () => {
    refetchAgents();
    onRefresh?.();
  };

  const handleCreateAgent = (data: CreateAgentFormData) => {
    const newAgent = createAgent({
      name: data.name,
      description: data.description,
      prompt: data.prompt,
      model: data.model,
    });
    toast({ description: `Agent "${data.name}" created successfully` });
    setShowCreateDialog(false);
    setSelectedAgentName(null);
    setSelectedLocalAgentId(newAgent.id);
  };

  const handleDeleteLocalAgent = (agentId: string) => {
    deleteAgent(agentId);
    toast({ description: "Agent deleted successfully" });
    if (selectedLocalAgentId === agentId) setSelectedLocalAgentId(null);
  };

  const handleSaveLangfuseAgentAsLocal = (agentData: {
    name: string;
    description: string;
    prompt: string;
    model: string;
    source_type: "langfuse";
    source_id: string;
  }) => {
    const existing = localAgents.find(
      a => a.source_type === "langfuse" && a.source_id === agentData.source_id
    );
    if (existing) {
      updateAgent(existing.id, {
        name: agentData.name,
        description: agentData.description,
        prompt: agentData.prompt,
        model: agentData.model,
      });
      setSelectedLocalAgentId(existing.id);
    } else {
      setSelectedLocalAgentId(createAgent(agentData).id);
    }
    setSelectedAgentName(null);
  };

  const handleSelectBackendAgent = (name: string) => {
    setSelectedAgentName(name);
    setSelectedLocalAgentId(null);
  };
  const handleSelectLocalAgent = (id: string) => {
    setSelectedLocalAgentId(id);
    setSelectedAgentName(null);
  };

  const agents = agentsData?.agents || {};
  const agentEntries = Object.entries(agents);

  // Get set of Langfuse agent names that have local overrides
  const localOverriddenAgentNames = new Set(
    localAgents
      .filter(agent => agent.source_type === "langfuse" && agent.source_id)
      .map(agent => agent.source_id!)
  );

  // Filter out Langfuse agents that have been saved locally
  const backendAgentsList = agentEntries
    .filter(([agentName]) => !localOverriddenAgentNames.has(agentName))
    .map(([agentName, details]) => ({
      ...details,
      agentKey: agentName,
    }));

  const totalAgents = backendAgentsList.length + localAgents.length;

  // Fetch selected backend agent details
  const { data: agentDetails, isLoading: agentDetailsLoading } = useQuery({
    queryKey: ["playgroundAgentDetails", selectedAgentName],
    queryFn: () =>
      import("../../services/evals").then(m =>
        m.getPlaygroundAgentDetails(selectedAgentName!, true)
      ),
    enabled: !!selectedAgentName,
  });

  const selectedLocalAgent = selectedLocalAgentId
    ? localAgents.find(agent => agent.id === selectedLocalAgentId)
    : null;

  return (
    <TabsContent value="playground-agents">
      <Stack direction="row" justify="between" align="center" className="al-mb-4">
        <Typography variant="body" weight="medium">
          Studio Agents
        </Typography>
        <Stack direction="row" gap="sm">
          <Button onClick={() => setShowCreateDialog(true)} size="sm">
            <IconPlus className="al-w-4 al-h-4 al-mr-2" />
            Create Agent
          </Button>
          <Button onClick={handleRefresh} variant="outline" size="sm">
            <IconRefresh className="al-w-4 al-h-4 al-mr-2" />
            Refresh
          </Button>
        </Stack>
      </Stack>

      {agentsLoading ? (
        <Loader label="Loading agents..." />
      ) : (
        <div className="al-grid al-grid-cols-12 al-gap-4">
          {/* Left Panel - Agents List */}
          <div className="al-col-span-4">
            <Card>
              <CardHeader>
                <Typography variant="body" weight="medium">
                  Local Agents ({totalAgents})
                </Typography>
              </CardHeader>
              <CardContent>
                <div className="al-space-y-2">
                  {/* Local Agents - Combined Section */}
                  {totalAgents > 0 ? (
                    <>
                      {localAgents.map(agent => (
                        <LocalAgentListItem
                          key={agent.id}
                          agent={agent}
                          isSelected={selectedLocalAgentId === agent.id}
                          onSelect={handleSelectLocalAgent}
                        />
                      ))}

                      {/* Backend Agents */}
                      {backendAgentsList.map(agent => (
                        <PlaygroundAgentListItem
                          key={agent.name}
                          agent={agent}
                          isSelected={selectedAgentName === agent.name}
                          onSelect={handleSelectBackendAgent}
                        />
                      ))}
                    </>
                  ) : (
                    <div className="al-text-center al-py-8 al-text-sm al-text-gray-500">
                      No agents available. Create your first agent!
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Agent Details */}
          <div className="al-col-span-8">
            <AgentDetailsPanel
              selectedLocalAgent={selectedLocalAgent ?? null}
              selectedAgentName={selectedAgentName}
              agentDetailsLoading={agentDetailsLoading}
              agentDetails={agentDetails}
              availableModels={availableModels?.models.map(m => m.id)}
              agentVersions={selectedLocalAgent ? getAgentVersions(selectedLocalAgent.id) : []}
              onDeleteLocalAgent={() =>
                selectedLocalAgent && handleDeleteLocalAgent(selectedLocalAgent.id)
              }
              onUpdateLocalAgent={updates =>
                selectedLocalAgent && updateAgent(selectedLocalAgent.id, updates)
              }
              onRestoreVersion={version =>
                selectedLocalAgent && restoreAgentVersion(selectedLocalAgent.id, version)
              }
              onSaveAsLocal={handleSaveLangfuseAgentAsLocal}
            />
          </div>
        </div>
      )}

      {/* Create Agent Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="al-max-w-3xl al-max-h-[90vh] al-overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Agent</DialogTitle>
          </DialogHeader>
          <CreateAgentForm
            onSubmit={handleCreateAgent}
            onCancel={() => setShowCreateDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </TabsContent>
  );
};
